import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createLogoutSender, resetIdleSignOutClock } from '@aaimplatform/ui';
import { IDLE_SIGN_OUT_CHANNEL } from './idleSignOut';
import type { components } from '../../../../packages/types/generated/platform-admin.d.ts';
import type { components as AuthComponents } from '../../../../packages/types/generated/core-authorization.d.ts';
import { csrfHeaders } from '../lib/csrf';

// Empty-string fallback per F-VITE-AUTH-LOCALHOST-DEFAULT-FEDERATED-SPAS.
// Missing build-arg produces a relative-URL fetch that 404s visibly on the SPA's
// own origin rather than silently issuing CORS requests to a user's localhost.
// See knowledge-graph/projects/aaim/technology-innovation/incidents/2026-05-13-ops-console-auth-url-localhost-bake.md
//
// browser-token-storage-hardening (SEC-V04, N6): the session now lives in an
// HttpOnly host-only cookie the backend sets on the login / exchange / refresh
// responses. For the browser to attribute that Set-Cookie to admin.aaim.com (so
// it rides every same-origin /api/* call), the auth endpoints MUST be served
// from the SPA's own origin -- which the empty-string default guarantees
// (relative URL = same-origin). A non-empty cross-origin VITE_AUTH_SERVICE_URL
// would land the cookie on the wrong host and break cookie auth.
const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL ?? '';

export type UserRole = components['schemas']['AuthUserRole'];
export type User = components['schemas']['PlatformUser'];

// Guards App.tsx's mount-effect initialize() against React StrictMode's
// double-invoke so GET /api/v1/auth/me fires once per page load.
let initializeStarted = false;

/**
 * Sign-out requests are sent until the server hears one, so a request lost to
 * a dropped network cannot leave the session for a reload to restore
 * (DCF-62 review, 2026-11-02). The marker it keeps is a flag, no credential.
 */
const logoutSender = createLogoutSender('admin-portal:pending-logout');

type LogoutResponse = AuthComponents['schemas']['LogoutResponse'];

/**
 * POST /api/v1/auth/logout. True only when aaim-core says the session ended
 * (200, LogoutResponse.success true); a 403 CSRF refusal, a 5xx or
 * success false leaves the sign-out pending.
 */
async function postLogout(sessionId: string | null): Promise<boolean> {
  // The auth-token cookie authenticates this POST, so it echoes the
  // csrf-token cookie as X-CSRF-Token (GH #4888; lib/csrf.ts).
  const res = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/logout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
    credentials: 'include',
    body: JSON.stringify({
      ...(sessionId ? { sessionId } : {}),
      type: 'jwt',
    }),
  });
  if (!res.ok) return false;
  const body = (await res.json().catch(() => null)) as LogoutResponse | null;
  return body?.success === true;
}

/**
 * Normalize the roles array on a `User` record to lowercase.
 *
 * Sprint C12FR (2026-04-30) tenet: every authz literal compare in this SPA
 * must match against lowercase. Two ingress paths populate `user.roles`:
 *
 *   1. /api/v1/auth/login / /api/v1/auth/me (proxy normalizer in
 *      platform-admin-api / aaim-core) -> already lowercase.
 *   2. /auth/callback code-exchange + GET /me hydration from aaim-core's
 *      OAuth2 redirect -> UPPERCASE because aaim-core's prisma-oauth2-user-repo
 *      emits 'AAIM_SUPER_ADMIN' for the platform-admin email allowlist.
 *
 * Without normalizing here, downstream consumers that grep for lowercase
 * literals (PortalLayout nav-group resolver, hasRole/hasAnyRole) silently
 * fail-closed for SSO platform admins. The screenshot symptom is "No
 * navigation items available for your role" with a healthy session.
 *
 * Reference: `.claude/rules/authz-case-insensitive.md`
 */
function normalizeRoles(user: User): User {
  if (!Array.isArray(user.roles)) return user;
  return {
    ...user,
    roles: user.roles.map((r) =>
      typeof r === 'string' ? (r.toLowerCase() as UserRole) : r,
    ),
  };
}

/**
 * Coerce the raw user shape returned by the auth service into the PlatformUser
 * type. aaim-core's mapToAuthenticatedUser populates `name` from the
 * UserIdentity profile join, but new identities without a profile fall through
 * to email; default `name` to email so the UI always has a label.
 */
function coerceUser(raw: User & { name?: string }): User {
  return {
    ...raw,
    name: raw.name && raw.name.length > 0 ? raw.name : raw.email,
  };
}

interface AuthState {
  user: User | null;
  // accessToken / refreshToken are VESTIGIAL post the N6 cookie cutover: the
  // session credential lives in the HttpOnly cookie, never in JS-readable
  // state. They are retained as `null` only so existing consumers
  // (routes/index.tsx, StaffLaunch, PortalLayout) keep compiling; no code path
  // writes a non-null bearer here, and they are never persisted to browser
  // storage. SEC-V04 / .claude/rules/no-browser-token-storage.md.
  accessToken: string | null;
  refreshToken: string | null;
  // Opaque session identifier (NOT a bearer credential) captured from the login
  // response so the logout POST can revoke the server-side session row. The
  // gate's token-key list intentionally excludes sessionId; persisting it is
  // gate-clean and enables a reliable cookie-clearing logout across reloads.
  sessionId: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  oauthLoading: boolean;
  oauthError: string | null;
  /**
   * Resolve the session on app load from the HttpOnly cookie via
   * GET /api/v1/auth/me, then flip isInitialized so the UI does not flash
   * logged-out. No token is read from browser storage.
   */
  initialize: () => void;
  /**
   * Hydrate the user record from GET /api/v1/auth/me using the HttpOnly session
   * cookie (credentials: 'include'). Returns true when a valid session was
   * found. Used by initialize() and by the OAuth/SSO callbacks after the code
   * exchange sets the cookie. With `refuseIfSignOutPending` (initialize()),
   * a sign-out marked while the read was in flight, by another tab, ends
   * this session too instead of being cleared by the restore.
   */
  hydrateSession: (options?: { refuseIfSignOutPending?: boolean }) => Promise<boolean>;
  /**
   * GH #1916: the credential-path login is TWO-STEP for TOTP tenants. A
   * totpRequired result means the store holds an in-memory (NEVER
   * persisted) challenge-scoped intermediate token and the UI renders the
   * code entry; totpEnrollmentRequired means the intermediate is
   * enroll-scoped and the UI renders the QR enrollment step.
   */
  login: (email: string, password: string) => Promise<{ totpRequired: boolean; totpEnrollmentRequired: boolean }>;
  /**
   * In-memory only (excluded from partialize -- the intermediate token
   * must never reach browser storage per no-browser-token-storage.md).
   * Short-lived by construction: the token TTL is 5 minutes.
   */
  pendingTotp: { intermediateToken: string; scope: 'totp-challenge' | 'totp-enroll' } | null;
  clearPendingTotp: () => void;
  /**
   * Complete the second factor with EXACTLY ONE of an authenticator code
   * or a single-use recovery code (GH #1916 device-loss path). On success
   * the HttpOnly session cookie is set by the service and the store is
   * authenticated. Returns recoveryCodesRemaining when the recovery leg ran.
   */
  submitTotpChallenge: (input: { code?: string; recoveryCode?: string }) => Promise<{ recoveryCodesRemaining?: number }>;
  /** Begin TOTP enrollment (enroll-scoped intermediate): QR + secret. */
  startTotpEnrollment: () => Promise<{ secret: string; qrCodeDataUrl: string; otpAuthUri: string }>;
  /**
   * Complete enrollment with the first authenticator code. Returns the
   * single-use recovery codes -- shown EXACTLY ONCE, never stored. The
   * caller then re-runs login() to obtain a challenge-scoped intermediate.
   */
  verifyTotpEnrollment: (code: string) => Promise<{ recoveryCodes: string[] }>;
  loginWithGoogle: () => Promise<void>;
  /**
   * Cookie-era hydration entry point (OAuth/SSO callbacks). The session lives
   * in the HttpOnly cookie set by the code exchange; any token fields on the
   * argument are ignored -- only the user record is applied.
   */
  loginWithTokens: (params: {
    user: User;
    accessToken?: string;
    refreshToken?: string;
  }) => void;
  /**
   * Cookie-era SSO hydration. The session lives in the HttpOnly cookie; the
   * token argument is ignored. Retained for callers that pass the legacy shape.
   */
  loginWithSSO: (params: {
    user: User;
    accessToken?: string;
  }) => void;
  /**
   * Apply the impersonation result returned from POST /api/v1/auth/impersonate.
   * The impersonate response also rotates the HttpOnly session cookie (the
   * caller fetches with credentials: 'include'), so only the user record is
   * applied here -- the token argument is ignored. The new user object carries
   * `impersonatedBy`, `originalTenantId`, and `apexTenantId` when impersonating
   * a non-apex tenant; returning to apex clears those fields.
   */
  setImpersonation: (params: {
    user: User;
    accessToken?: string;
  }) => void;
  logout: () => void;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  /**
   * True when the current session is impersonating a non-apex tenant. Driven
   * by the `impersonatedBy` field on the user. Used by the SPA to gate the
   * red tenant name + dim username visual indicators.
   */
  isImpersonating: () => boolean;
}

/**
 * Authentication store using Zustand.
 *
 * Session model (browser-token-storage-hardening N6, SEC-V04):
 * - Auth credential lives in an HttpOnly + Secure + SameSite cookie set by the
 *   backend on login / exchange / refresh. The SPA holds NO bearer token.
 * - Every authed request is sent with credentials: 'include' so the cookie
 *   rides it; the @aaimplatform/database cookie->Bearer shim reads it
 *   server-side. The SPA never attaches an Authorization header.
 * - On reload the session is re-derived from GET /api/v1/auth/me, not from
 *   browser storage. Only the user record (+ opaque sessionId) is persisted.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      sessionId: null,
      isAuthenticated: false,
      isInitialized: false,
      oauthLoading: false,
      oauthError: null,

      /**
       * Resolve the session from the HttpOnly cookie on app load.
       */
      initialize: () => {
        if (initializeStarted) return;
        initializeStarted = true;
        if (logoutSender.pending()) {
          // A sign-out that never reached the server: send it again, and do
          // not restore the session it was meant to end.
          const { sessionId } = get();
          logoutSender.send(() => postLogout(sessionId));
          resetIdleSignOutClock(IDLE_SIGN_OUT_CHANNEL);
          set({ user: null, sessionId: null, isAuthenticated: false, isInitialized: true });
          return;
        }
        void get()
          .hydrateSession({ refuseIfSignOutPending: true })
          .then((restored) => {
            // No session: the next sign-in starts a full inactivity period.
            if (!restored) resetIdleSignOutClock(IDLE_SIGN_OUT_CHANNEL);
          })
          .finally(() => set({ isInitialized: true }));
      },

      hydrateSession: async (options) => {
        try {
          const res = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/me`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          if (!res.ok) {
            set({ user: null, isAuthenticated: false });
            return false;
          }
          const data = (await res.json()) as { user?: User & { name?: string } };
          if (!data.user) {
            set({ user: null, isAuthenticated: false });
            return false;
          }
          // Another tab signed out while /me was in flight: send its sign-out
          // and restore nothing, since a restore would clear the marker before
          // that sign-out reaches the server.
          if (options?.refuseIfSignOutPending && logoutSender.pending()) {
            const { sessionId } = get();
            logoutSender.send(() => postLogout(sessionId));
            set({ user: null, sessionId: null, isAuthenticated: false });
            return false;
          }
          set({ user: normalizeRoles(coerceUser(data.user)), isAuthenticated: true });
          return true;
        } catch {
          set({ user: null, isAuthenticated: false });
          return false;
        }
      },

      /**
       * Login with email and password against the canonical aaim-core
       * authorization service at POST /api/v1/auth/login (proxied same-origin
       * so the HttpOnly session cookie is attributed to the SPA host).
       *
       * Sprint D Lane D1 (2026-05-17, F-XPORTAL-MOCK-JWT-001 closure): the
       * prior implementation set `accessToken: 'mock-jwt-access-token'` and a
       * fabricated user record. This method calls the real auth service, which
       * mints a real session and sets the HttpOnly cookie. Pre-customer atomic
       * transition per `.claude/rules/no-pre-customer-tech-debt.md`: NO fallback
       * to the prior mock token.
       *
       * N6 cookie cutover: `credentials: 'include'` honors the Set-Cookie; the
       * response body tokens (still emitted during the dual-delivery window) are
       * intentionally NOT stored -- the session lives only in the cookie.
       */
      login: async (email: string, password: string) => {
        const response = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            type: 'jwt',
            email,
            password,
          }),
        });

        if (!response.ok) {
          const errorData = (await response
            .json()
            .catch(() => null)) as { error?: string; errorCode?: string } | null;
          const message = errorData?.error ?? `Login failed (${response.status})`;
          throw new Error(message);
        }

        const data = (await response.json()) as {
          success?: boolean;
          user?: User & { name?: string };
          sessionId?: string;
          totpRequired?: boolean;
          totpEnrollmentRequired?: boolean;
        };

        // The cookie -- not a body token -- is the credential, so we no longer
        // require `accessToken` in the body (it is stripped post-N3b). Require
        // only a successful, user-bearing result.
        if (!data.success || !data.user) {
          throw new Error('Authorization service returned an incomplete login result');
        }

        // A TOTP-pending response sets no full session cookie; do not mark
        // the operator authenticated. Stash the intermediate token IN MEMORY
        // (excluded from partialize) and hand the step to the login UI
        // (GH #1916: admin-portal now renders the inline challenge/enroll
        // step, matching advisor-tools).
        if (data.totpRequired || data.totpEnrollmentRequired) {
          const intermediate = (data as { intermediateToken?: string }).intermediateToken;
          if (!intermediate) {
            throw new Error('TOTP is required but the login response carried no intermediate token');
          }
          set({
            pendingTotp: {
              intermediateToken: intermediate,
              scope: data.totpEnrollmentRequired ? 'totp-enroll' : 'totp-challenge',
            },
          });
          return {
            totpRequired: Boolean(data.totpRequired) && !data.totpEnrollmentRequired,
            totpEnrollmentRequired: Boolean(data.totpEnrollmentRequired),
          };
        }

        set({
          user: normalizeRoles(coerceUser(data.user)),
          sessionId: data.sessionId ?? null,
          isAuthenticated: true,
          pendingTotp: null,
        });
        return { totpRequired: false, totpEnrollmentRequired: false };
      },

      pendingTotp: null,

      clearPendingTotp: () => set({ pendingTotp: null }),

      submitTotpChallenge: async (input: { code?: string; recoveryCode?: string }) => {
        const pending = get().pendingTotp;
        if (!pending || pending.scope !== 'totp-challenge') {
          throw new Error('No pending TOTP challenge -- start the sign-in again');
        }
        const response = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/totp/challenge`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${pending.intermediateToken}`,
          },
          credentials: 'include',
          body: JSON.stringify(input.recoveryCode !== undefined ? { recoveryCode: input.recoveryCode } : { code: input.code }),
        });
        if (!response.ok) {
          const errorData = (await response.json().catch(() => null)) as { message?: string; code?: string } | null;
          throw new Error(errorData?.message ?? `Verification failed (${response.status})`);
        }
        const data = (await response.json()) as {
          user?: User & { name?: string };
          recoveryCodesRemaining?: number;
        };
        if (!data.user) {
          throw new Error('TOTP challenge did not return a user');
        }
        // Session cookie set by the service; establish identity from the body.
        set({
          user: normalizeRoles(coerceUser(data.user)),
          isAuthenticated: true,
          pendingTotp: null,
        });
        return {
          ...(data.recoveryCodesRemaining !== undefined
            ? { recoveryCodesRemaining: data.recoveryCodesRemaining }
            : {}),
        };
      },

      startTotpEnrollment: async () => {
        const pending = get().pendingTotp;
        if (!pending || pending.scope !== 'totp-enroll') {
          throw new Error('No pending TOTP enrollment -- start the sign-in again');
        }
        const response = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/totp/enroll`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${pending.intermediateToken}`,
          },
          credentials: 'include',
        });
        if (!response.ok) {
          const errorData = (await response.json().catch(() => null)) as { message?: string } | null;
          throw new Error(errorData?.message ?? `Enrollment failed (${response.status})`);
        }
        return (await response.json()) as { secret: string; qrCodeDataUrl: string; otpAuthUri: string };
      },

      verifyTotpEnrollment: async (code: string) => {
        const pending = get().pendingTotp;
        if (!pending || pending.scope !== 'totp-enroll') {
          throw new Error('No pending TOTP enrollment -- start the sign-in again');
        }
        const response = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/totp/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${pending.intermediateToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });
        if (!response.ok) {
          const errorData = (await response.json().catch(() => null)) as { message?: string } | null;
          throw new Error(errorData?.message ?? `Enrollment verification failed (${response.status})`);
        }
        const data = (await response.json()) as { recoveryCodes?: string[] };
        // Enrollment is complete; the enroll-scoped intermediate is spent.
        // The caller re-runs login() for a challenge-scoped intermediate.
        set({ pendingTotp: null });
        return { recoveryCodes: data.recoveryCodes ?? [] };
      },

      /**
       * Initiate Google OAuth2 login flow.
       * Calls the authorization service to get the Google consent URL,
       * then redirects the browser. The callback is handled by OAuthCallback page.
       */
      loginWithGoogle: async () => {
        set({ oauthLoading: true, oauthError: null });

        try {
          const response = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/oauth2/authorize`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              provider: 'google',
              callbackUrl: `${window.location.origin}/auth/callback`,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
              errorData?.error?.message ?? `Authorization request failed (${response.status})`
            );
          }

          const data = await response.json() as { authorizationUrl: string; state: string };

          // Store state for CSRF verification on callback
          sessionStorage.setItem('oauth_state', data.state);

          // Redirect browser to Google consent screen
          window.location.href = data.authorizationUrl;
        } catch (error) {
          set({
            oauthLoading: false,
            oauthError: error instanceof Error ? error.message : 'OAuth login failed',
          });
        }
      },

      /**
       * Apply the user record from an OAuth callback. The session credential is
       * the HttpOnly cookie set by the code exchange; any token argument is
       * ignored.
       */
      loginWithTokens: ({ user }) => {
        set({
          user: normalizeRoles(user),
          isAuthenticated: true,
          oauthLoading: false,
          oauthError: null,
        });
      },

      /**
       * Apply the user record from an SSO callback. The session credential is
       * the HttpOnly cookie; the token argument is ignored.
       */
      loginWithSSO: ({ user }) => {
        set({
          user: normalizeRoles(user),
          isAuthenticated: true,
          oauthLoading: false,
          oauthError: null,
        });
      },

      /**
       * Apply an impersonation result. The impersonate response rotates the
       * HttpOnly session cookie; only the impersonated user record is applied.
       */
      setImpersonation: ({ user }) => {
        set({
          user: normalizeRoles(user),
          isAuthenticated: true,
          oauthLoading: false,
          oauthError: null,
        });
      },

      /**
       * Logout: revoke the server-side session and clear the HttpOnly cookies
       * via POST /api/v1/auth/logout, then clear local state. Local state
       * clears at once so the UI logs out immediately; the request is sent
       * again until the server hears it (logoutSender), and initialize() does
       * not restore a session whose sign-out is still pending. `sessionId` is still sent but is NO LONGER LOAD-BEARING:
       * since GH #2701 (101b89727c) the backend derives the session to revoke
       * from the VERIFIED auth-token cookie this request already carries via
       * credentials:'include', and ignores body.sessionId. The field is kept
       * only until that fix reaches the deployed aaim-core-authz revision --
       * the currently-serving revision still revokes the body-named id, so
       * dropping it before that deploy would leave logout with no server-side
       * revoke. Drop it once the deploy lands.
       */
      logout: () => {
        const { sessionId } = get();
        // Local state clears at once; the request is retried until heard.
        logoutSender.send(() => postLogout(sessionId));

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          sessionId: null,
          isAuthenticated: false,
          oauthLoading: false,
          oauthError: null,
        });
      },

      /**
       * Check if user has specific role. Comparison is case-insensitive
       * per the C12FR authz tenet -- the parameter and stored roles are
       * both lowercased before comparison so callers can pass either case
       * and SSO-issued UPPERCASE roles still match lowercase literals.
       */
      hasRole: (role: UserRole) => {
        const { user } = get();
        if (!user?.roles) return false;
        const target = typeof role === 'string' ? role.toLowerCase() : role;
        return user.roles.some((r) =>
          typeof r === 'string' ? r.toLowerCase() === target : r === target,
        );
      },

      /**
       * Check if user has any of the specified roles. Comparison is
       * case-insensitive (see hasRole).
       */
      hasAnyRole: (roles: UserRole[]) => {
        const { user } = get();
        if (!user?.roles) return false;
        const targets = roles.map((r) =>
          typeof r === 'string' ? r.toLowerCase() : r,
        );
        return user.roles.some((r) =>
          typeof r === 'string'
            ? targets.includes(r.toLowerCase() as UserRole)
            : targets.includes(r),
        );
      },

      /**
       * True when the current session is impersonating a non-apex tenant.
       * Driven by the `impersonatedBy` field returned from the impersonate
       * endpoint and persisted on the User record. Used by PortalLayout to
       * gate the OrgSwitcher and UserProfileMenu visual indicators.
       */
      isImpersonating: () => {
        const { user } = get();
        return !!user?.impersonatedBy;
      },
    }),
    {
      name: 'aaim-auth-storage',
      // Bumped to v3 in the N6 cookie cutover (SEC-V04) so any existing v2
      // session that persisted accessToken / refreshToken in localStorage is
      // discarded on reload -- the bearer token must never survive in browser
      // storage. v2 was the C12FR (2026-04-30) UPPERCASE-roles wipe.
      version: 3,
      // Persist only non-secret fields: the user record (for instant paint,
      // re-confirmed by GET /me) and the opaque sessionId (for logout revoke).
      // NEVER accessToken / refreshToken -- enforced by
      // check-no-browser-token-storage.sh. See .claude/rules/no-browser-token-storage.md.
      partialize: (state) => ({
        user: state.user,
        sessionId: state.sessionId,
      }),
    }
  )
);

// A new sign-in clears a sign-out still pending from the previous session, so
// a late retry (it carries this browser's cookies) never ends the new one.
// initialize() never authenticates while one is pending, so any change to
// authenticated is a sign-in.
useAuthStore.subscribe((state, prev) => {
  if (state.isAuthenticated && !prev.isAuthenticated) logoutSender.clear();
});
